import type { ReactNode } from "react";
import { cn } from "../../lib/utils";

type HeadlineTag = "h1" | "h2" | "h3";
type HeadlineSize = "hero" | "section" | "subsection";
type HeadlineVariant = "default" | "inverse";
type AccentLayout = "inline" | "block";

export type DisplayHeadlineProps = {
  as?: HeadlineTag;
  size?: HeadlineSize;
  variant?: HeadlineVariant;
  align?: "left" | "center";
  eyebrow?: string;
  accent?: ReactNode;
  accentLayout?: AccentLayout;
  className?: string;
  html?: string;
  children?: ReactNode;
};

const sizeClasses: Record<HeadlineSize, string> = {
  hero: "text-4xl md:text-5xl lg:text-6xl",
  section: "text-3xl md:text-4xl",
  subsection: "text-2xl md:text-3xl font-semibold",
};

const variantClasses: Record<HeadlineVariant, { base: string; accent: string; eyebrow: string }> = {
  default: {
    base: "text-gray-900 dark:text-gray-100",
    accent: "text-wwwuxmichalcomdodger-blue italic font-semibold",
    eyebrow:
      "text-[11px] md:text-xs font-semibold tracking-[0.2em] text-wwwuxmichalcomdodger-blue uppercase",
  },
  inverse: {
    base: "text-white",
    accent: "text-white italic font-semibold",
    eyebrow: "text-[11px] md:text-xs font-semibold tracking-[0.2em] text-white uppercase",
  },
};

export function DisplayHeadline({
  as: Tag = "h1",
  size = "hero",
  variant = "default",
  align = "left",
  eyebrow,
  accent,
  accentLayout = "inline",
  className,
  html,
  children,
}: DisplayHeadlineProps): JSX.Element {
  const colors = variantClasses[variant];
  const accentEl = accent ? (
    <span className={colors.accent}>
      {accentLayout === "block" ? (
        <>
          <br />
          {accent}
        </>
      ) : (
        accent
      )}
    </span>
  ) : null;

  const tagClassName = cn(
    "font-playfair leading-tight",
    sizeClasses[size],
    colors.base,
  );

  return (
    <div className={cn(align === "center" && "text-center", className)}>
      {eyebrow && <p className={cn(colors.eyebrow, "mb-4 md:mb-6")}>{eyebrow}</p>}
      {html ? (
        <Tag className={tagClassName} dangerouslySetInnerHTML={{ __html: html }} />
      ) : (
        <Tag className={tagClassName}>
          {children}
          {accentEl}
        </Tag>
      )}
    </div>
  );
}
